import { format } from 'date-fns-tz';

import { REASON_FOR_UPDATING_USER } from './helpers';
import { initialState } from './useAuthSlice';
import {
  compPoints as fetchCompPoints,
  deletePlayerAccount,
  logout as logoutRequest,
  player as fetchPlayer,
  playerAccounts as fetchPlayerAccounts,
  setPlayerAccounts,
  User,
  Wallet,
} from '@/api/auth';
import { Currency } from '@/api/common';
import { amountWithCurrencySymbol } from '@/helpers/currency';
import { setGroupsForNewcomer } from '@/helpers/user';
import { saveUserData } from '@/helpers/userData';
import { sendOtherSignOutWithoutClickEvent } from '@/modules/googleAnalytics/events/otherEvents';
import { STORAGE, useCommonStore, useLocalStorageStore } from '@/store';

const IS_USER_FETCHED_FIRST_TIME = 'is_user_fetched_first_time';

export const fetchUser = async (
  reasonForUpdatingUser: REASON_FOR_UPDATING_USER,
  countryCode?: string,
  isActiveSessionBackend?: boolean
) => {
  const set = useCommonStore.setState;
  const { setUserWallets } = useCommonStore.getState();

  try {
    set({
      pendingUser: true,
      ...(reasonForUpdatingUser === REASON_FOR_UPDATING_USER.VISIT && {
        pendingUserPure: true,
      }),
    });

    const { data: user } = await fetchPlayer();

    const isAuthenticated = user?.id != null;

    // Passive logged out during offline
    if (
      !isAuthenticated &&
      isActiveSessionBackend &&
      sessionStorage.getItem(IS_USER_FETCHED_FIRST_TIME) !== 'true'
    ) {
      sendOtherSignOutWithoutClickEvent();
    }
    sessionStorage.setItem(IS_USER_FETCHED_FIRST_TIME, 'true');
    //

    if (isAuthenticated) {
      set({ pendingWallets: true, compPointsPending: true });

      let wallets: Wallet[] = [];

      const [{ data: dataWallets }, { data: compPoints }] = await Promise.all([
        fetchPlayerAccounts(),
        fetchCompPoints(),
      ]);

      wallets = dataWallets || [];

      if (wallets.length === 0 && user?.currency != null) {
        const { data: dataUpdatedWallet } = await setPlayerAccounts(
          user.currency
        );

        if (dataUpdatedWallet) {
          wallets = [dataUpdatedWallet];
        }
      }

      saveUserData({ email: user.email });

      set({
        user: setGroupsForNewcomer(user, countryCode),
        reasonForUpdatingUser,
        wallets,
        isAuthenticated,
        compPoints,
      });
      setUserWallets(wallets);

      return;
    }

    set({
      ...initialState,
      guestLanguage: user?.language,
      reasonForUpdatingUser,
    });
  } catch {
    set({
      ...initialState,
      reasonForUpdatingUser,
    });
  } finally {
    set({
      pendingUser: false,
      pendingUserPure: false,
      pendingWallets: false,
      compPointsPending: false,
    });
  }
};

export const refreshUser = async (prevUser?: User, countryCode?: string) => {
  try {
    const set = useCommonStore.setState;

    const { data: user } = await fetchPlayer();

    // Expired/broken session while tab is open
    if (!user?.id) {
      sendOtherSignOutWithoutClickEvent();
    }

    if (JSON.stringify(prevUser?.statuses) === JSON.stringify(user?.statuses))
      return;
    const updatedUser = setGroupsForNewcomer(user, countryCode);
    updatedUser && set({ user: updatedUser });
  } catch (error) {
    console.log(error);
  }
};

export const insertUser = async (
  user: User,
  reasonForUpdatingUser: REASON_FOR_UPDATING_USER
) => {
  const set = useCommonStore.setState;
  const { setUserWallets } = useCommonStore.getState();

  saveUserData({ email: user.email });

  set({ pendingWallets: true });

  try {
    let wallets: Wallet[] = [];
    const [{ data: dataWallets }, { data: compPoints }] = await Promise.all([
      fetchPlayerAccounts(),
      fetchCompPoints(),
    ]);

    wallets = dataWallets || [];

    if (wallets.length === 0 && user?.currency != null) {
      const { data: dataUpdatedWallet } = await setPlayerAccounts(
        user.currency
      );

      if (dataUpdatedWallet) {
        wallets = [dataUpdatedWallet];
      }
    }

    set({
      user: setGroupsForNewcomer(user),
      reasonForUpdatingUser,
      isAuthenticated: user.id != null,
      compPoints,
    });
    setUserWallets(wallets);
  } catch (error) {
    console.log(error);
  } finally {
    set({ pendingWallets: false });
  }
};

export const resetUser = async (callback?: () => void) => {
  const set = useCommonStore.setState;

  try {
    set({ pendingUser: true });
    await logoutRequest();

    set({
      ...initialState,
      recentGamesIds: [],
      recentGames: [],
    });

    callback && callback();
  } catch (error) {
    console.log(error);
  } finally {
    set({ pendingUser: false, pendingWallets: false });
  }
};

export const changeUserWallet = async (
  currency: Wallet['currency'],
  countryCode?: string
) => {
  const set = useCommonStore.setState;

  try {
    set({ pendingWallets: true });
    await setPlayerAccounts(currency);
    await fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT, countryCode);
  } catch (error) {
    console.log(error);
  } finally {
    set({ pendingWallets: false });
  }
};

export const deleteUserWallet = async (
  prevCurrency: Wallet['currency'],
  nextCurrency?: Wallet['currency'],
  countryCode?: string
) => {
  const set = useCommonStore.setState;

  try {
    set({ pendingWallets: true });
    await deletePlayerAccount(prevCurrency);
    nextCurrency && (await setPlayerAccounts(nextCurrency));
    await fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT, countryCode);
  } catch {
    set({ pendingWallets: false });
  }
};

export const formatAmount = (
  currencies: Currency[],
  amount: number,
  userCurrency?: string,
  withSymbol?: boolean
) => {
  const currentCurrency = currencies.find(({ code }) => code === userCurrency);

  if (currentCurrency == null) return amount;

  if (!withSymbol || !userCurrency)
    return amount / currentCurrency.subunitsToUnit;

  return amountWithCurrencySymbol(
    userCurrency,
    amount / currentCurrency.subunitsToUnit
  );
};

export const unFormatAmount = (
  currencies: Currency[],
  amount: number,
  userCurrency?: string
) => {
  const currentCurrency = currencies.find(({ code }) => code === userCurrency);

  if (currentCurrency == null) return amount;

  return amount * currentCurrency.subunitsToUnit;
};

export const setEmailConfirmationPopupSeen = () => {
  const set = useCommonStore.setState;

  useLocalStorageStore
    .getState()
    .replace(
      STORAGE.LAST_SEEN_EMAIL_CONFIRMATION_POPUP,
      format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSXXX")
    );
  set({ isEmailConfirmationPopup: false });
};

export const updatePlayerWallet = async (
  currency: Wallet['currency'],
  user: User
) => {
  try {
    const { setUserWallets } = useCommonStore.getState();
    useCommonStore.setState({ pendingWallets: true });

    const { data: wallet } = await setPlayerAccounts(currency);

    if (!wallet) return;

    useCommonStore.setState({
      user: { ...user, currency },
    });
    setUserWallets([wallet]);
  } catch (error) {
    console.log(error);
  } finally {
    useCommonStore.setState({ pendingWallets: false });
  }
};
